import { type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../providers/AuthProvider';
import { hasModuleAccess } from './roles';
import { LoadingSpinner, COLORS } from '../ui';

interface ProtectedRouteProps {
  children: ReactNode;
  /** Role prefix required to access this module (e.g. "Sales", "Ops", "Catalog") */
  appRolePrefix?: string;
}

/**
 * Route-level access gate. Redirects unauthenticated users to /login and
 * shows an Access Denied page for authenticated users who lack the
 * required module role.
 *
 * Sits inside each module's root layout so that direct URL access to
 * `/sales/*`, `/ops/*`, `/catalog/*` is gated even when the launcher
 * tile is hidden.
 */
export function ProtectedRoute({ children, appRolePrefix }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, roles, displayName, logout } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return <LoadingSpinner fullPage label="Checking authentication..." />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (appRolePrefix && !hasModuleAccess(roles, appRolePrefix)) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          padding: '24px',
          textAlign: 'center',
          backgroundColor: '#FFF1E0',
        }}
      >
        <div style={{ fontSize: '24px', fontWeight: 700, marginBottom: '12px', color: '#553746' }}>
          Access Denied
        </div>
        <div
          style={{
            fontSize: '15px',
            color: COLORS.textSecondary,
            maxWidth: '400px',
            lineHeight: '1.6',
            marginBottom: '24px',
          }}
        >
          Hi {displayName ?? 'there'} — your account doesn't have access to Vera {appRolePrefix}.
          Contact your administrator to request the <strong>{appRolePrefix}.User</strong> role.
        </div>
        <button
          onClick={() => {
            logout();
          }}
          style={{
            padding: '8px 24px',
            fontSize: '14px',
            cursor: 'pointer',
            backgroundColor: '#553746',
            color: '#fff',
            border: 'none',
            borderRadius: '6px',
          }}
        >
          Sign out
        </button>
      </div>
    );
  }

  return <>{children}</>;
}
